import type { IComponentController, IScope } from "angular";
import "angular-translate";

import { isEmpty } from "lodash";

import type { IAlarmCentral, IOperatorStationSlim, IResponseWithLoading } from "App/Modules/Models";
import type { IAlarmCentralsRepository, IEquipmentRepository } from "App/Modules/Repositories";
import type { IFormContainerResolve } from "App/Modules/Services";
import { extendWithLoadingAndFail } from "App/Modules/Services/ObservableExtensions";

enum AddOperatorStationDialogStates {
    SelectOperatorStation,
    DefineOperatorStationParameters,
}

export default class AddOperatorStationToAlarmCentralComponentController implements IComponentController {
    public static $inject: string[] = ["$scope", "$translate", "mmAlarmCentralsRepository", "mmEquipmentRepository"];

    public alarmCentral: IAlarmCentral;

    public state: AddOperatorStationDialogStates = AddOperatorStationDialogStates.SelectOperatorStation;

    public operatorStations: IResponseWithLoading<IOperatorStationSlim[]>;

    public displayValidationError = false;

    public createResponse: IResponseWithLoading<IOperatorStationSlim>;

    public patchResponse: IResponseWithLoading<IOperatorStationSlim>;

    public isNew: boolean;

    public newOperatorStation: IOperatorStationSlim;

    public existingOperatorStation: IOperatorStationSlim;

    public resolve: IFormContainerResolve<IOperatorStationSlim, IAlarmCentral>;

    constructor(
        private readonly $scope: IScope,
        private readonly $translate: angular.translate.ITranslateService,
        private readonly alarmCentralsRepository: IAlarmCentralsRepository,
        private readonly equipmentRepository: IEquipmentRepository,
    ) {}

    public $onInit() {
        this.alarmCentral = this.resolve.bindings;

        if (this.alarmCentral) {
            this.$scope.$watch("$ctrl.selectedOperatorStation", (newValue) => {
                if (newValue !== undefined) {
                    this.displayValidationError = false;
                }
            });
        }
    }

    public selectExistingOperatorStation(operatorStation: IOperatorStationSlim) {
        this.isNew = false;
        this.existingOperatorStation = operatorStation;
    }

    public createNewOperatorStation(searchString: string) {
        this.isNew = true;
        this.newOperatorStation = {
            serialNumber: searchString,
        } as IOperatorStationSlim;

        this.state = AddOperatorStationDialogStates.DefineOperatorStationParameters;
    }

    public add() {
        if (this.isNew) {
            const newOp = {
                serialNumber: this.newOperatorStation.serialNumber,
                alarmCentralId: this.alarmCentral.id,
            } as IOperatorStationSlim;

            extendWithLoadingAndFail(this.equipmentRepository.addOperatorStation(newOp)).subscribe(
                (response) => {
                    this.createResponse = response;

                    if (!response.isLoading && response.data) {
                        this.existingOperatorStation = response.data;
                        this.patchOperatorStation(response.data);
                    }
                },
                (error) => {
                    this.createResponse = error;
                    this.displayValidationError = true;
                },
            );
        } else {
            this.patchOperatorStation(this.existingOperatorStation);
        }
    }

    public patchOperatorStation(selectedOperatorStation: IOperatorStationSlim) {
        const operatorStationPatch = {} as IOperatorStationSlim;

        if (selectedOperatorStation) {
            operatorStationPatch.alarmCentralId = this.alarmCentral.id;
        }

        if (!isEmpty(operatorStationPatch)) {
            extendWithLoadingAndFail(
                this.equipmentRepository.patchOperatorStation(selectedOperatorStation.id, operatorStationPatch),
            ).subscribe(
                (response) => {
                    this.patchResponse = response;
                    if (!response.isLoading && response.data) {
                        this.resolve.options.onOk(response.data);
                    }
                },
                (error) => {
                    this.patchResponse = error;
                    this.displayValidationError = true;
                },
            );
        }
    }

    public cancel() {
        if (this.resolve.options.onCancel) {
            this.resolve.options.onCancel();
        }
    }
}
